/**
 * Pack accuracy — Settings admin surface (DC-1517, v3.0 Block 3 org lane).
 *
 * The visible half of the override-telemetry story: how often a Domain
 * Pack's judgments survive human review. Spec:
 * docs/design/v3.0-org-lane-surfaces-ux-spec.md §2 — "honest rates, visible
 * denominators": accuracy is a proxy (1 − override_rate), the view says so
 * in its own labels, every rate renders beside its n, and low-n cohorts are
 * discounted in shape AND words (never hidden — hiding weeks fabricates a
 * smoother story).
 *
 * Data: GET /api/cv-judgments/telemetry/summary?pack_ref=&weeks= —
 * `OverrideTelemetry { per_pack, weekly, per_rule (worst-first),
 * per_subdomain }`. Read-only; no mutating control exists on this surface.
 *
 * Tokens only (lint:cream `MIGRATED`); chart colors are read from the live
 * CSS custom properties at runtime (trajectoryChartOptions precedent).
 * ApexCharts is lazy-loaded so it stays out of the settings entry chunk.
 */

import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ApexOptions } from 'apexcharts';
import { Target } from 'lucide-react';

import { useApiClient } from '../../hooks/useApiClient';
import '../../styles/pack-accuracy.css';

const ReactApexChart = React.lazy(() => import('react-apexcharts'));

// ============================================================================
// Contract (mirrors server/domainPacks/judgmentOverrides.ts)
// ============================================================================

interface PackOverrideStats {
  pack_ref: string;
  judgments: number;
  overridden_judgments: number;
  override_rows: number;
  override_rate: number;
}

interface WeeklyOverrideStats {
  week_start: string;
  judgments: number;
  overridden_judgments: number;
  override_rate: number;
}

interface RuleOverrideStats {
  rule_id: string;
  judgments_with_rule: number;
  overridden_judgments: number;
  override_rate: number;
}

interface SubdomainOverrideStats {
  subdomain: string;
  judgments: number;
  overridden_judgments: number;
  override_rate: number;
}

interface OverrideTelemetry {
  per_pack: PackOverrideStats[];
  weekly: WeeklyOverrideStats[];
  per_rule: RuleOverrideStats[];
  per_subdomain: SubdomainOverrideStats[];
}

interface TelemetryResponse {
  telemetry: OverrideTelemetry;
  window_weeks: number;
  pack_ref: string | null;
}

// ============================================================================
// Constants (spec §2)
// ============================================================================

/** Cohorts/rows below this n are discounted in shape and words. */
const LOW_N_THRESHOLD = 5;
/** Per-rule alarm: rate ≥ this AND n ≥ LOW_N earns the one status accent. */
const RULE_ALARM_RATE = 0.5;
/** Tables cap at this many visible rows; the rest sits behind "Show all N". */
const ROW_CAP = 10;
/** Window options in weeks; 12 is the API default. */
const WINDOW_OPTIONS = [4, 12, 26, 52];
/** Under this many weekly cohorts the chart gives way to a stat card. */
const MIN_COHORTS_FOR_CHART = 4;

type LoadState = 'loading' | 'error' | 'ready';

const pct = (rate: number): string => `${Math.round(rate * 100)}%`;
const accuracyPct = (overrideRate: number): string => pct(1 - overrideRate);

function fmtWeek(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ============================================================================
// Chart options (tokens read at runtime — zero raw hex, lint:cream stays green)
// ============================================================================

interface ChartTokens {
  teal: string;
  slate: string;
  taupe: string;
  ink: string;
  card: string;
}

function readChartTokens(): ChartTokens {
  const root = getComputedStyle(document.documentElement);
  const v = (name: string): string => root.getPropertyValue(name).trim();
  return {
    teal: v('--tri-teal'),
    slate: v('--tri-slate'),
    taupe: v('--tri-taupe'),
    ink: v('--tri-ink'),
    card: v('--tri-card'),
  };
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * One teal series, 0–100% y-axis, no legend. Low-n weeks render as hollow
 * discrete markers (shape, not opacity alone) and their tooltip carries a
 * `low n` note — visually discounted, never hidden.
 */
function buildAccuracyChartOptions(
  tokens: ChartTokens,
  weeks: WeeklyOverrideStats[],
): ApexOptions {
  const lowNMarkers = weeks
    .map((w, i) => ({ w, i }))
    .filter(({ w }) => w.judgments < LOW_N_THRESHOLD)
    .map(({ i }) => ({
      seriesIndex: 0,
      dataPointIndex: i,
      fillColor: tokens.card,
      strokeColor: tokens.slate,
      size: 5,
    }));

  return {
    chart: {
      type: 'area',
      toolbar: { show: false },
      animations: { enabled: !prefersReducedMotion() },
      fontFamily: 'inherit',
      foreColor: tokens.slate,
    },
    colors: [tokens.teal],
    stroke: { curve: 'straight', width: 2 },
    fill: { type: 'solid', opacity: 0.15 },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: { borderColor: tokens.taupe, strokeDashArray: 3 },
    markers: { size: 4, strokeWidth: 0, discrete: lowNMarkers },
    xaxis: {
      categories: weeks.map((w) => fmtWeek(w.week_start)),
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 4,
      labels: { formatter: (val: number) => `${Math.round(val)}%` },
    },
    tooltip: {
      custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
        const w = weeks[dataPointIndex];
        if (!w) return '';
        const lowN = w.judgments < LOW_N_THRESHOLD ? ' · low n' : '';
        return (
          `<div class="pka-tooltip">` +
          `<div class="pka-tooltip__week">Week of ${fmtWeek(w.week_start)}${lowN}</div>` +
          `<div class="pka-tooltip__value">${accuracyPct(w.override_rate)} accuracy</div>` +
          `<div class="pka-tooltip__n">${w.judgments} judgments · ${w.overridden_judgments} overridden</div>` +
          `</div>`
        );
      },
    },
  };
}

// ============================================================================
// Shared table anatomy (spec §2.4)
// ============================================================================

interface RateRow {
  key: string;
  /** First-column label; mono when the id is machine-flavored. */
  label: string;
  labelMono: boolean;
  labelMuted?: boolean;
  n: number;
  overridden: number;
  rate: number;
  /** Per-rule alarm ink on the rate cell only. */
  alarm?: boolean;
}

function RateMeter({ rate }: { rate: number }): React.ReactElement {
  return (
    <span className="pka-meter" aria-hidden="true">
      <span className="pka-meter__fill" style={{ width: `${Math.round(rate * 100)}%` }} />
    </span>
  );
}

function RateTable({
  title,
  caption,
  nHeader,
  rows,
  emptyText,
}: {
  title: string;
  caption: string;
  nHeader: string;
  rows: RateRow[];
  emptyText: string;
}): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, ROW_CAP);

  return (
    <section className="pka-table-card">
      <h3 className="pka-table-title">{title}</h3>
      {rows.length === 0 ? (
        <p className="pka-table-empty">{emptyText}</p>
      ) : (
        <>
          <table className="pka-table">
            <caption className="pka-visually-hidden">{caption}</caption>
            <thead>
              <tr>
                <th scope="col">{title}</th>
                <th scope="col" className="pka-num">{nHeader}</th>
                <th scope="col" className="pka-num">Overridden</th>
                <th scope="col" className="pka-rate-col">Override rate</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const lowN = row.n < LOW_N_THRESHOLD;
                return (
                  <tr key={row.key}>
                    <td className={row.labelMono ? 'pka-mono' : undefined}>
                      {row.labelMuted ? <em className="pka-muted">{row.label}</em> : row.label}
                    </td>
                    <td className="pka-num">{row.n}</td>
                    <td className="pka-num">{row.overridden}</td>
                    <td className="pka-rate-col">
                      <span
                        className={
                          row.alarm && !lowN
                            ? 'pka-rate pka-rate--alarm'
                            : lowN
                              ? 'pka-rate pka-rate--low-n'
                              : 'pka-rate'
                        }
                      >
                        {pct(row.rate)}
                        {lowN && <span className="pka-low-n-tag">low n</span>}
                      </span>
                      <RateMeter rate={row.rate} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length > ROW_CAP && (
            <button
              type="button"
              className="pka-text-btn"
              onClick={() => setExpanded((e) => !e)}
            >
              {expanded ? 'Show fewer' : `Show all ${rows.length}`}
            </button>
          )}
        </>
      )}
    </section>
  );
}

// ============================================================================
// The view
// ============================================================================

export const PackAccuracy: React.FC = () => {
  const { authFetch } = useApiClient();
  const [state, setState] = useState<LoadState>('loading');
  const [data, setData] = useState<TelemetryResponse | null>(null);
  const [packRef, setPackRef] = useState<string>('');
  const [weeks, setWeeks] = useState<number>(12);
  const [chartAsTable, setChartAsTable] = useState(false);
  /** Pack options from the first unfiltered load, so the select stays stable
   *  while a filter is active. */
  const [packOptions, setPackOptions] = useState<string[]>([]);

  // Stale-response guard (DC-1593 pattern).
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setState('loading');
    try {
      const params = new URLSearchParams({ weeks: String(weeks) });
      if (packRef) params.set('pack_ref', packRef);
      const res = await authFetch(`/api/cv-judgments/telemetry/summary?${params.toString()}`);
      if (requestSeq.current !== seq) return;
      if (!res.ok) throw new Error(`telemetry request failed (${res.status})`);
      const body = (await res.json()) as TelemetryResponse;
      if (requestSeq.current !== seq) return;
      setData(body);
      if (!packRef) {
        setPackOptions(body.telemetry.per_pack.map((p) => p.pack_ref).sort());
      }
      setState('ready');
    } catch {
      if (requestSeq.current !== seq) return;
      setState('error');
    }
  }, [authFetch, packRef, weeks]);

  useEffect(() => {
    void load();
  }, [load]);

  const weekly = useMemo(() => data?.telemetry.weekly ?? [], [data]);
  const totalJudgments = useMemo(
    () => (data?.telemetry.per_pack ?? []).reduce((acc, p) => acc + p.judgments, 0),
    [data],
  );

  const chart = useMemo(() => {
    if (weekly.length < MIN_COHORTS_FOR_CHART || chartAsTable) return null;
    if (typeof window === 'undefined') return null;
    return {
      options: buildAccuracyChartOptions(readChartTokens(), weekly),
      series: [
        {
          name: 'Accuracy',
          data: weekly.map((w) => Math.round((1 - w.override_rate) * 100)),
        },
      ],
    };
  }, [weekly, chartAsTable]);

  // ------------------------------------------------------------------ states

  if (state === 'loading') {
    return (
      <div className="pka" aria-busy="true">
        <PackAccuracyHeader />
        <div className="pka-skeleton-row" role="status" aria-label="Loading pack accuracy">
          <div className="pka-skeleton pka-skeleton--filters" />
          <div className="pka-skeleton pka-skeleton--chart" />
          <div className="pka-skeleton pka-skeleton--table" />
          <div className="pka-skeleton pka-skeleton--table" />
          <div className="pka-skeleton pka-skeleton--table" />
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="pka">
        <PackAccuracyHeader />
        <div className="pka-error" role="alert">
          <span>Couldn’t load override telemetry.</span>
          <button type="button" className="pka-text-btn" onClick={() => void load()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty window: no judgments at all — one designed empty state, no zeroed
  // chart (spec §2.5).
  if (totalJudgments === 0) {
    return (
      <div className="pka">
        <PackAccuracyHeader />
        <Filters
          packRef={packRef}
          packOptions={packOptions}
          weeks={weeks}
          onPackChange={setPackRef}
          onWeeksChange={setWeeks}
        />
        <div className="pka-empty">
          <Target size={28} aria-hidden="true" className="pka-empty__icon" />
          <p>
            No CV judgments in this window. Judgments accumulate as Contextual Validity runs on
            pack-governed domains.
          </p>
        </div>
      </div>
    );
  }

  const telemetry = data!.telemetry;
  const latest = weekly.length > 0 ? weekly[weekly.length - 1] : null;
  const prior = weekly.length > 1 ? weekly[weekly.length - 2] : null;

  return (
    <div className="pka">
      <PackAccuracyHeader />
      <Filters
        packRef={packRef}
        packOptions={packOptions}
        weeks={weeks}
        onPackChange={setPackRef}
        onWeeksChange={setWeeks}
      />

      {/* Hero: weekly accuracy */}
      <section className="pka-hero" aria-label="Override-informed accuracy, weekly">
        <div className="pka-hero-head">
          <h3 className="pka-table-title">Accuracy over time</h3>
          {weekly.length >= MIN_COHORTS_FOR_CHART && (
            <button
              type="button"
              className="pka-text-btn"
              onClick={() => setChartAsTable((v) => !v)}
            >
              {chartAsTable ? 'View as chart' : 'View as table'}
            </button>
          )}
        </div>

        {weekly.length === 0 && (
          <p className="pka-table-empty">No judgments dated within this window yet.</p>
        )}

        {weekly.length > 0 && weekly.length < MIN_COHORTS_FOR_CHART && latest && (
          <div className="pka-stat-card">
            <span className="pka-stat-card__value">{accuracyPct(latest.override_rate)}</span>
            <span className="pka-stat-card__label">
              week of {fmtWeek(latest.week_start)} · {latest.judgments} judgments ·{' '}
              {latest.overridden_judgments} overridden
              {latest.judgments < LOW_N_THRESHOLD && ' · low n'}
            </span>
            {prior && (
              <span className="pka-stat-card__delta">
                {(() => {
                  const delta =
                    Math.round((1 - latest.override_rate) * 100) -
                    Math.round((1 - prior.override_rate) * 100);
                  const sign = delta > 0 ? '+' : '';
                  return `${sign}${delta} pts vs week of ${fmtWeek(prior.week_start)}`;
                })()}
              </span>
            )}
          </div>
        )}

        {chart && (
          <Suspense fallback={<div className="pka-skeleton pka-skeleton--chart" />}>
            <ReactApexChart options={chart.options} series={chart.series} type="area" height={220} />
          </Suspense>
        )}

        {chartAsTable && weekly.length >= MIN_COHORTS_FOR_CHART && (
          <table className="pka-table">
            <caption className="pka-visually-hidden">
              Weekly override-informed accuracy for the selected window
            </caption>
            <thead>
              <tr>
                <th scope="col">Week</th>
                <th scope="col" className="pka-num">Judgments</th>
                <th scope="col" className="pka-num">Overridden</th>
                <th scope="col" className="pka-num">Accuracy</th>
              </tr>
            </thead>
            <tbody>
              {weekly.map((w) => (
                <tr key={w.week_start}>
                  <td>{fmtWeek(w.week_start)}</td>
                  <td className="pka-num">{w.judgments}</td>
                  <td className="pka-num">{w.overridden_judgments}</td>
                  <td className="pka-num">
                    {accuracyPct(w.override_rate)}
                    {w.judgments < LOW_N_THRESHOLD && (
                      <span className="pka-low-n-tag">low n</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Per-pack */}
      <RateTable
        title="Pack"
        caption="Judgments and override rate per Domain Pack in the selected window"
        nHeader="Judgments"
        rows={telemetry.per_pack.map((p) => ({
          key: p.pack_ref,
          label: p.pack_ref,
          labelMono: true,
          n: p.judgments,
          overridden: p.overridden_judgments,
          rate: p.override_rate,
        }))}
        emptyText="No packs produced judgments in this window."
      />

      <div className="pka-table-pair">
        {/* Per-rule, worst first (delivered order preserved) */}
        <RateTable
          title="Rule"
          caption="Override rate per rule, worst first"
          nHeader="Judgments with rule"
          rows={telemetry.per_rule.map((r) => ({
            key: r.rule_id,
            label: r.rule_id,
            labelMono: true,
            n: r.judgments_with_rule,
            overridden: r.overridden_judgments,
            rate: r.override_rate,
            alarm: r.override_rate >= RULE_ALARM_RATE && r.judgments_with_rule >= LOW_N_THRESHOLD,
          }))}
          emptyText="No overrides recorded — nothing to rank yet."
        />

        {/* Per-subdomain */}
        <RateTable
          title="Subdomain"
          caption="Override rate per subdomain"
          nHeader="Judgments"
          rows={telemetry.per_subdomain.map((s) => ({
            key: s.subdomain || '(unclassified)',
            label: s.subdomain || '(unclassified)',
            labelMono: false,
            labelMuted: !s.subdomain,
            n: s.judgments,
            overridden: s.overridden_judgments,
            rate: s.override_rate,
          }))}
          emptyText="No overrides recorded — nothing to rank yet."
        />
      </div>
    </div>
  );
};

// ============================================================================
// Header + filters
// ============================================================================

function PackAccuracyHeader(): React.ReactElement {
  return (
    <header className="pka-header">
      <div className="pka-eyebrow">Domain packs</div>
      <h2 className="pka-title">Pack accuracy</h2>
      <p className="pka-subtitle">
        Accuracy is override-informed: 1 − the share of judgments later corrected by a reviewer.
        It measures agreement with reviewers, not ground truth.
      </p>
    </header>
  );
}

function Filters({
  packRef,
  packOptions,
  weeks,
  onPackChange,
  onWeeksChange,
}: {
  packRef: string;
  packOptions: string[];
  weeks: number;
  onPackChange: (v: string) => void;
  onWeeksChange: (v: number) => void;
}): React.ReactElement {
  return (
    <div className="pka-filters">
      <label className="pka-filter">
        <span className="pka-filter__label">Pack</span>
        <select value={packRef} onChange={(e) => onPackChange(e.target.value)}>
          <option value="">All packs</option>
          {packOptions.map((ref) => (
            <option key={ref} value={ref}>
              {ref}
            </option>
          ))}
        </select>
      </label>
      <label className="pka-filter">
        <span className="pka-filter__label">Window</span>
        <select value={weeks} onChange={(e) => onWeeksChange(Number(e.target.value))}>
          {WINDOW_OPTIONS.map((w) => (
            <option key={w} value={w}>
              {w} weeks
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

export default PackAccuracy;
